<template>
  <div class="max-w-4xl mx-auto">
    <!-- Page Header -->
    <div class="mb-8">
      <h2 class="text-2xl font-bold text-gray-900 mb-2">Patient Self-Registration QR Code</h2>
      <p class="text-gray-600">
        Generate a QR code for patients to self-register at your facility. Display this QR code at reception or waiting areas.
      </p>
    </div>

    <!-- Generate QR Code Section -->
    <div v-if="!qrData" class="bg-white rounded-lg shadow-md p-8">
      <div class="text-center">
        <div class="mx-auto w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <svg class="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
          </svg>
        </div>

        <h3 class="text-xl font-semibold text-gray-900 mb-2">
          Generate Your QR Code
        </h3>
        <p class="text-gray-600 mb-6">
          Click the button below to generate a secure QR code for patient self-registration.
        </p>

        <button
          @click="generateQRCode"
          :disabled="isGenerating"
          class="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          <span v-if="isGenerating" class="flex items-center">
            <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Generating...
          </span>
          <span v-else class="flex items-center">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Generate QR Code
          </span>
        </button>
      </div>
    </div>

    <!-- QR Code Display Section -->
    <div v-else class="space-y-6">
      <!-- Color Customization Panel -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h4 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <svg class="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
          Customize QR Code Colors
        </h4>

        <!-- Color Presets -->
        <div class="mb-4">
          <p class="text-sm text-gray-600 mb-2">Quick Presets:</p>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="preset in colorPresets"
              :key="preset.name"
              @click="applyColorPreset(preset)"
              class="px-4 py-2 rounded-lg border-2 transition-all hover:shadow-md"
              :style="{
                backgroundColor: preset.light,
                borderColor: preset.dark,
                color: preset.dark
              }"
            >
              {{ preset.name }}
            </button>
          </div>
        </div>

        <!-- Custom Color Pickers -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              QR Code Color (Dark Squares)
            </label>
            <div class="flex items-center space-x-3">
              <input
                type="color"
                v-model="qrColors.dark"
                @input="renderQRCode"
                class="h-10 w-20 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                v-model="qrColors.dark"
                @input="renderQRCode"
                class="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                placeholder="#000000"
              />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Background Color
            </label>
            <div class="flex items-center space-x-3">
              <input
                type="color"
                v-model="qrColors.light"
                @input="renderQRCode"
                class="h-10 w-20 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                v-model="qrColors.light"
                @input="renderQRCode"
                class="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                placeholder="#FFFFFF"
              />
            </div>
          </div>
        </div>

        <!-- Color Tips -->
        <div class="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p class="text-sm text-blue-800">
            <span class="font-medium">Tip:</span> Use high contrast colors for better scanning. Test with your phone camera before printing.
          </p>
        </div>
      </div>

      <!-- QR Code Card -->
      <div class="bg-white rounded-lg shadow-md p-8">
        <div class="flex flex-col items-center">
          <!-- Facility Info -->
          <div class="text-center mb-6">
            <h3 class="text-2xl font-bold text-gray-900 mb-2">{{ qrData.facility_name }}</h3>
            <p class="text-gray-600">Patient Self-Registration QR Code</p>
          </div>

          <!-- QR Code Image -->
          <div class="relative mb-6">
            <div class="p-6 bg-white border-4 border-gray-200 rounded-lg shadow-lg">
              <canvas ref="qrCanvas" class="w-64 h-64"></canvas>
            </div>
          </div>

          <!-- Instructions -->
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 max-w-md">
            <div class="flex items-start">
              <svg class="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
              </svg>
              <div class="text-sm text-blue-800">
                <p class="font-medium mb-1">How to use:</p>
                <ol class="list-decimal list-inside space-y-1">
                  <li>Download or print this QR code</li>
                  <li>Display at your reception or waiting area</li>
                  <li>Patients scan to self-register</li>
                  <li>Registration data goes directly to your system</li>
                </ol>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex flex-wrap justify-center gap-4">
            <button
              @click="downloadQRCode"
              class="px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center"
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download QR Code
            </button>

            <button
              @click="printQRCode"
              class="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print QR Code
            </button>

            <button
              @click="regenerateQRCode"
              class="px-6 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 transition-colors flex items-center"
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Regenerate
            </button>
          </div>
        </div>
      </div>

      <!-- QR Code Details -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h4 class="text-lg font-semibold text-gray-900 mb-4">QR Code Details</h4>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p class="text-sm text-gray-500">Facility</p>
            <p class="font-medium text-gray-900">{{ qrData.facility_name }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Generated</p>
            <p class="font-medium text-gray-900">{{ new Date().toLocaleDateString() }}</p>
          </div>
          <div class="md:col-span-2">
            <p class="text-sm text-gray-500">Registration URL</p>
            <p class="font-mono text-xs text-gray-700 bg-gray-50 p-2 rounded break-all">{{ qrData.qr_code_url }}</p>
          </div>
        </div>
      </div>

      <!-- Security Notice -->
      <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div class="flex items-start">
          <svg class="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          <div class="text-sm text-yellow-800">
            <p class="font-medium mb-1">Security Notice</p>
            <p>This QR code is cryptographically signed and linked to your facility only. It cannot be used to register patients at other facilities.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode';

export default {
  name: 'FacilityQRCode',

  data() {
    return {
      qrData: null,
      isGenerating: false,

      // Color customization
      qrColors: {
        dark: '#000000',   // QR code foreground
        light: '#FFFFFF'   // QR code background
      },

      // Predefined color schemes
      colorPresets: [
        { name: 'Classic', dark: '#000000', light: '#ffffff' },
        { name: 'Professional', dark: '#1e3a8a', light: '#ffffff' },
        { name: 'Vibrant', dark: '#dc2626', light: '#fef2f2' },
        { name: 'Nature', dark: '#15803d', light: '#f0fdf4' },
        { name: 'Purple', dark: '#7e22ce', light: '#faf5ff' }
      ],

      // Store facility town for header
      facilityTown: null
    };
  },

  methods: {
    async generateQRCode() {
      this.isGenerating = true;

      try {
        const user = JSON.parse(localStorage.getItem('user'));

        if (!user || !user.FacilityId) {
          this.$toast.error('Facility information not found. Please log in again.');
          return;
        }

        // Call backend to generate QR code
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/facilities/${user.FacilityId}/generate_qr_code`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        });

        const result = await response.json();

        if (response.ok && result.success) {
          this.qrData = result.data;

          // Store facility town from verify_qr_code response (if available)
          // You'll need to add this to the generate_qr_code response
          this.facilityTown = result.data.nearest_town || null;

          // Generate QR code on canvas after DOM update
          await this.$nextTick();
          await this.renderQRCode();

          this.$toast.success('QR Code generated successfully!');
        } else {
          this.$toast.error(result.message || 'Failed to generate QR code');
        }

      } catch (error) {
        console.error('Error generating QR code:', error);
        this.$toast.error('Failed to generate QR code. Please try again.');
      } finally {
        this.isGenerating = false;
      }
    },

    async renderQRCode() {
      if (!this.qrData || !this.$refs.qrCanvas) return;

      try {
        await QRCode.toCanvas(this.$refs.qrCanvas, this.qrData.qr_code_url, {
          width: 256,
          margin: 2,
          color: {
            dark: this.qrColors.dark,
            light: this.qrColors.light
          }
        });
      } catch (error) {
        console.error('Error rendering QR code:', error);
        this.$toast.error('Failed to render QR code');
      }
    },

    applyColorPreset(preset) {
      this.qrColors.dark = preset.dark;
      this.qrColors.light = preset.light;
      this.renderQRCode();
    },

    async createStyledQRImage() {
      // Create a canvas with header, QR code, and footer
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Dimensions
      const qrSize = 512;
      const headerHeight = 160;  // Increased from 140 to fit "Patient Self-Registration"
      const footerHeight = 100;
      const padding = 50;
      const totalWidth = qrSize + (padding * 2);
      const totalHeight = headerHeight + qrSize + footerHeight + (padding * 2);

      canvas.width = totalWidth;
      canvas.height = totalHeight;

      // Background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Header Line 1 - "Patient Self-Registration"
      ctx.fillStyle = '#3b82f6';  // Blue color
      ctx.font = 'bold 28px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Patient Self-Registration', totalWidth / 2, 45);

      // Header Line 2 - Facility Name
      ctx.fillStyle = '#1f2937';  // Dark gray
      ctx.font = 'bold 36px Arial, sans-serif';
      ctx.fillText(this.qrData.facility_name, totalWidth / 2, 90);

      // Header Line 3 - Nearest Town (if available)
      if (this.facilityTown) {
        ctx.fillStyle = '#6b7280';  // Medium gray
        ctx.font = '24px Arial, sans-serif';
        ctx.fillText(this.facilityTown, totalWidth / 2, 125);
      }

      // Horizontal line separator (optional decorative element)
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(padding, headerHeight - 10);
      ctx.lineTo(totalWidth - padding, headerHeight - 10);
      ctx.stroke();

      // QR Code
      const tempCanvas = document.createElement('canvas');
      await QRCode.toCanvas(tempCanvas, this.qrData.qr_code_url, {
        width: qrSize,
        margin: 2,
        color: {
          dark: this.qrColors.dark,
          light: this.qrColors.light
        }
      });

      ctx.drawImage(tempCanvas, padding, headerHeight + padding);

      // Footer - Instructions
      const footerY = headerHeight + qrSize + padding + 40;
      ctx.fillStyle = '#4b5563';
      ctx.font = '18px Arial, sans-serif';
      ctx.fillText('Scan with your phone camera to register', totalWidth / 2, footerY);

      // Footer - Branding (darker and more prominent)
      ctx.fillStyle = '#4b5563';  // Dark gray instead of light gray
      ctx.font = 'italic 18px Arial, sans-serif';  // Slightly larger font
      ctx.fillText('Powered by JiJi Health', totalWidth / 2, footerY + 35);

      return canvas;
    },

    async downloadQRCode() {
      if (!this.qrData) return;

      try {
        const styledCanvas = await this.createStyledQRImage();

        // Convert to blob and download
        styledCanvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${this.qrData.facility_name.replace(/\s+/g, '-')}-QR-Code.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          this.$toast.success('QR Code downloaded successfully!');
        });

      } catch (error) {
        console.error('Error downloading QR code:', error);
        this.$toast.error('Failed to download QR code');
      }
    },

    async printQRCode() {
      if (!this.qrData) return;

      try {
        const styledCanvas = await this.createStyledQRImage();
        const imageData = styledCanvas.toDataURL('image/png');

        // Create print window with styled content
        const printWindow = window.open('', '_blank');

        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>QR Code - ${this.qrData.facility_name}</title>
            <style>
              @page {
                size: A4;
                margin: 2cm;
              }

              body {
                font-family: Arial, sans-serif;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
                margin: 0;
                padding: 20px;
                background: white;
              }

              .container {
                text-align: center;
                max-width: 700px;
              }

              .qr-image {
                width: 600px;
                height: auto;
                margin: 0 auto;
                display: block;
              }

              .instructions {
                text-align: left;
                padding: 20px;
                background: #f3f4f6;
                border-radius: 8px;
                margin-top: 30px;
              }

              .instructions h3 {
                margin-top: 0;
                color: #1f2937;
              }

              .instructions ol {
                margin: 10px 0;
                padding-left: 20px;
              }

              .instructions li {
                margin: 8px 0;
                color: #4b5563;
                line-height: 1.6;
              }

              @media print {
                body {
                  background: white;
                }

                .instructions {
                  page-break-before: avoid;
                }
              }
            </style>
          </head>
          <body>
            <div class="container">
              <img src="${imageData}" alt="QR Code" class="qr-image" />

              <div class="instructions">
                <h3>How to Register:</h3>
                <ol>
                  <li>Open your phone's camera or QR code scanner app</li>
                  <li>Point the camera at this QR code</li>
                  <li>Tap the notification that appears on your screen</li>
                  <li>Fill in your personal details in the registration form</li>
                  <li>Submit the form and note your patient code</li>
                  <li>Proceed to the reception desk with your patient code</li>
                </ol>
              </div>
            </div>
          </body>
          </html>
        `);

        printWindow.document.close();
        printWindow.focus();

        // Wait for content to load before printing
        setTimeout(() => {
          printWindow.print();
        }, 500);

      } catch (error) {
        console.error('Error printing QR code:', error);
        this.$toast.error('Failed to print QR code');
      }
    },

    regenerateQRCode() {
      if (confirm('Are you sure you want to regenerate the QR code? The current QR code will still work.')) {
        this.qrData = null;
        this.facilityTown = null;
        // Reset colors to default
        this.qrColors.dark = '#000000';
        this.qrColors.light = '#FFFFFF';
        this.generateQRCode();
      }
    }
  },

  mounted() {
    // Optionally auto-generate QR code on mount
    // this.generateQRCode();
  }
};
</script>

<style scoped>
/* Ensure QR code canvas renders properly */
canvas {
  display: block;
}

/* Color input styling */
input[type="color"] {
  cursor: pointer;
}

input[type="color"]::-webkit-color-swatch-wrapper {
  padding: 2px;
}

input[type="color"]::-webkit-color-swatch {
  border: 1px solid #d1d5db;
  border-radius: 4px;
}
</style>